import { CommonModule } from '@angular/common';
import type { TemplateRef, SimpleChanges } from '@angular/core';
import { Component, Input, ViewChild, ChangeDetectorRef, output } from '@angular/core';
import { RouterModule } from '@angular/router';

import type { UserIdMap } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import type { KeysAreSubset } from '@recruitee/common';
import { trackByProp, NgTemplateAssertType } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import type { LocationBrief } from '@recruitee/locations-types';
import type { JobsIdMap } from '@recruitee/offers-data';
import type { OfferBrief } from '@recruitee/offers-types';
import type { Requisition } from '@recruitee/requisitions-types';
import { RequisitionStatus, RequisitionColumnId } from '@recruitee/requisitions-types';
import type {
  DataTableCellContext,
  DataTableColumnGeometryChange,
  DataTableRow,
} from '@recruitee/user-interface';
import {
  AdminsAvatarsComponent,
  AvatarComponent,
  ButtonComponent,
  DataTableComponent,
  DateComponent,
  OfferStatusIndicatorComponent,
  TruncateComponent,
} from '@recruitee/user-interface';
import { camelCase } from '@recruitee/utils';

import { RequisitionTableRowActionsComponent } from './requisition-table-row-actions/requisition-table-row-actions.component';
import type { RequisitionsTableColumn } from '../../../requisitions.types';
import { RequisitionsCommonTextsService } from '../../../services/requisitions-common-texts.service';
import { RequisitionsStatusBadgeComponent } from '../../requisitions-status-badge/requisitions-status-badge.component';

export type RequisitionListRowOfferBrief = Pick<
  OfferBrief,
  'id' | 'status' | 'slug' | 'title' | 'kind'
>;

// minimal required Requisition type to display requistion row
export type RequisitionListRow = Pick<
  Requisition,
  | 'title'
  | 'status'
  | 'archivedAt'
  | 'departmentName'
  | 'createdAt'
  | 'remote'
  | 'location'
  | 'followerIds'
  | 'hiresCount'
  | 'openingsCount'
  | 'recruiterId'
  | 'hiringManagerId'
  | 'openings'
  | 'id'
  | 'guid'
  | 'stateName'
  | 'followed'
> &
  KeysAreSubset<
    Requisition,
    {
      offers: RequisitionListRowOfferBrief[];
      locations?: LocationBrief[];
      approvalRequest?: Pick<NonNullable<Requisition['approvalRequest']>, 'requesterId'>;
    }
  > & {
    visibleOffers?: RequisitionListRowOfferBrief[];
    visibleLocations?: LocationBrief[];
  };

const ExpandableCellGroups = {
  offers: [RequisitionColumnId.Jobs],
  locations: [RequisitionColumnId.LocationName, RequisitionColumnId.LocationAddress],
};

type RequisitionDataTableRowLimits = Record<keyof typeof ExpandableCellGroups, number>;

@Component({
  selector: 'rt-requisitions-list-table',
  templateUrl: './requisitions-list-table.component.html',
  styleUrls: ['./requisitions-list-table.component.less'],
  imports: [
    CommonModule,
    RequisitionsStatusBadgeComponent,
    RouterModule,
    RequisitionTableRowActionsComponent,
    DataTableComponent,
    DateComponent,
    OfferStatusIndicatorComponent,
    AvatarComponent,
    TranslatePipe,
    AdminsAvatarsComponent,
    ButtonComponent,
    TruncateComponent,
    NgTemplateAssertType,
  ],
})
export class RequisitionsListTableComponent {
  public readonly RequisitionTableCellContext: DataTableCellContext<
    Requisition & {
      visibleOffers: RequisitionListRowOfferBrief[];
      visibleLocations: LocationBrief[];
    }
  >;

  @Input() public requisitions: RequisitionListRow[];
  @Input() public jobsMap: JobsIdMap;
  @Input() public usersMap: UserIdMap;
  @Input() public canEditRequisitions: boolean;
  @Input() public columns: RequisitionsTableColumn[];
  @Input() public autoFit: boolean;
  @Input() public selectedIds: Record<number, boolean> = {};
  @Input() public selectable = true;
  @Input() public rowsToPaintAtStart = 25;

  public openRequisition = output<number>();
  public showJobsList = output<OfferBrief[]>();
  public follow = output<number>();
  public unfollow = output<number>();
  public duplicate = output<number>();
  public archive = output<Requisition>();
  public retrieve = output<Requisition>();
  public delete = output<Requisition>();
  public cancelRequest = output<number>();
  public addJob = output<number>();
  public sortChange = output<RequisitionsTableColumn>();
  public geometryChange = output<DataTableColumnGeometryChange>();
  public rowSelect = output<{
    row: DataTableRow;
    selected: boolean;
  }>();
  public changeGroupVisibility = output<string>();

  public RequisitionStatus: typeof RequisitionStatus = RequisitionStatus;
  public trackOfferById = trackByProp<OfferBrief>({ prop: 'id' });
  public trackLocationById = trackByProp<LocationBrief>({ prop: 'id' });
  public toCamelCase = camelCase;
  public cellTemplates: { [key: string]: TemplateRef<DataTableCellContext<Requisition>> };
  public followersPerRequisitionMap: Record<number, User[]> = {};
  public locationPerRequisitionMap: Record<number, string> = {};
  public reasonsForOpeningPerRequisitionMap: Record<number, string> = {};
  public expandedListItems: Record<string, Record<number, boolean>> = {};
  public expandableCellGroups = ExpandableCellGroups;
  public requisitionsRows: RequisitionListRow[] = [];

  @ViewChild('title', { static: true }) private title: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('status', { static: true }) private status: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('department', { static: true }) private department: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('dateCreated', { static: true }) private dateCreated: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('location', { static: true }) private location: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('jobs', { static: true }) private jobs: TemplateRef<DataTableCellContext<Requisition>>;
  @ViewChild('followers', { static: true }) private followers: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('hires', { static: true }) private hires: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('recruiter', { static: true }) private recruiter: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('hiringManager', { static: true }) private hiringManager: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('requester', { static: true }) private requester: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('reasonForOpening', { static: true }) private reasonForOpening: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('manage', { static: true }) private manage: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('locationName', { static: true }) private locationName: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('locationAddress', { static: true }) private locationAddress: TemplateRef<
    DataTableCellContext<Requisition>
  >;
  @ViewChild('workModel', { static: true }) private workModel: TemplateRef<
    DataTableCellContext<Requisition>
  >;

  private limits: RequisitionDataTableRowLimits = {
    offers: 4,
    locations: 4,
  };

  constructor(
    private requisitionsCommonTexts: RequisitionsCommonTextsService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.columns) {
      this.buildExpandedListItems();
    }

    if (changes.requisitions?.currentValue || changes.usersMap?.currentValue) {
      const locationPerRequisitionMap: Record<number, string> = {};
      const reasonsForOpeningPerRequisitionMap: Record<number, string> = {};
      const followersPerRequisitionMap: Record<number, User[]> = {};

      this.requisitions.forEach(req => {
        locationPerRequisitionMap[req.id] = this.requisitionsCommonTexts.getLocationText(req);
        reasonsForOpeningPerRequisitionMap[req.id] =
          this.requisitionsCommonTexts.getReasonForOpeningsText(req.openings);
        followersPerRequisitionMap[req.id] = req.followerIds
          .map(userId => this.usersMap[userId])
          .filter(v => v);
      });
      this.locationPerRequisitionMap = locationPerRequisitionMap;
      this.reasonsForOpeningPerRequisitionMap = reasonsForOpeningPerRequisitionMap;
      this.followersPerRequisitionMap = followersPerRequisitionMap;
      this.requisitionsRows = this.buildRowsFromRequisitions();
    }
  }

  public ngOnInit(): void {
    this.cellTemplates = {
      [RequisitionColumnId.Title]: this.title,
      [RequisitionColumnId.Status]: this.status,
      [RequisitionColumnId.Department]: this.department,
      [RequisitionColumnId.DateCreated]: this.dateCreated,
      [RequisitionColumnId.Jobs]: this.jobs,
      [RequisitionColumnId.Followers]: this.followers,
      [RequisitionColumnId.Hires]: this.hires,
      [RequisitionColumnId.Recruiter]: this.recruiter,
      [RequisitionColumnId.HiringManager]: this.hiringManager,
      [RequisitionColumnId.Requester]: this.requester,
      [RequisitionColumnId.ReasonForOpening]: this.reasonForOpening,
      [RequisitionColumnId.Manage]: this.manage,
      [RequisitionColumnId.LocationName]: this.locationName,
      [RequisitionColumnId.LocationAddress]: this.locationAddress,
      [RequisitionColumnId.WorkModel]: this.workModel,
    };
  }

  public hasAccessToJob(id: number): boolean {
    return !!this.jobsMap[id];
  }

  public expandList(group: keyof typeof ExpandableCellGroups, id: number) {
    this.toggleCellsList(true, id, ...this.expandableCellGroups[group]);
    this.cdRef.detectChanges();
  }

  public collapseList(group: keyof typeof ExpandableCellGroups, id: number) {
    this.toggleCellsList(false, id, ...this.expandableCellGroups[group]);
    this.cdRef.detectChanges();
  }

  private toggleCellsList(value: boolean, id: number, ...columnIds: string[]) {
    columnIds.forEach(columnId => {
      this.expandedListItems[columnId] = this.expandedListItems[columnId] ?? {};
      this.expandedListItems[columnId][id] = value;
    });
  }

  private buildExpandedListItems() {
    const columnIds = this.columns.map(c => c.id);
    this.expandedListItems = {
      ...this.expandedListItems,
      ...columnIds.reduce((acc, c) => (acc[c] ? acc : { ...acc, [c]: {} }), this.expandedListItems),
    };
  }

  private buildRowsFromRequisitions(): RequisitionListRow[] {
    return this.requisitions.map(requisition => ({
      ...requisition,
      offers: requisition.offers || [],
      visibleOffers: (requisition.offers || []).slice(0, this.limits.offers),
      locations: requisition.locations || [],
      visibleLocations: (requisition.locations || []).slice(0, this.limits.locations),
    }));
  }
}
